import { CreateClaimProvider, CreateClaimProviderType, SupportProvider } from '../../../../api';
import { Field, Formik, FormikProps } from 'formik';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import _ from 'lodash';
import { ClaimEditorStore } from '../../../../stores/claim-editor';
import * as Yup from 'yup';
import * as S from './additional-providers-form.styles';
import * as CS from '../section.styles';
import { BaseButton } from '../../../base';
import { FormErrorBanner } from '../../../base/form/form-error-banner';
import { InputField } from '../../../base/form';
import { ProviderAutocomplete } from './provider-autocomplete';

interface ProviderFormValues {
    providerLookup?: string;
    organization: string;
    first: string;
    last: string;
    npi: string;
    phone: string;
    fax: string;
    providerId?: string;
}

const IDENTITY_ERROR_MESSAGE = 'Either Organization or both First and Last name are required';

const ProviderSchema = Yup.object().shape({
    organization: Yup.string().test(
        'organization-or-first-last',
        IDENTITY_ERROR_MESSAGE,
        function (value) {
            const { first, last } = this.parent as ProviderFormValues;
            const organization = (value || '').trim();
            const firstName = (first || '').trim();
            const lastName = (last || '').trim();
            const hasOrganization = !!organization;
            const hasFullName = !!firstName && !!lastName;
            return hasOrganization || hasFullName;
        },
    ),
    first: Yup.string(),
    last: Yup.string(),
    npi: Yup.string().required('NPI is required').length(10, 'NPI must be exactly 10 characters'),
});

const initialValues: ProviderFormValues = {
    organization: '',
    first: '',
    last: '',
    npi: '',
    phone: '',
    fax: '',
    providerId: undefined,
};

interface IAdditionalProvidersFormProps {
    claimEditorStore: ClaimEditorStore;
}

export const AdditionalProvidersForm: React.FunctionComponent<IAdditionalProvidersFormProps> = observer(props => {
    const { claimEditorStore } = props;

    const handleAddProvider = useCallback((type: CreateClaimProviderType) => {
        const currentProviders = claimEditorStore?.providers || [];
        const newProvider: CreateClaimProvider = {
            first: undefined,
            middle: undefined,
            last: undefined,
            suffix: undefined,
            npi: undefined,
            phone: undefined,
            fax: undefined,
            providerId: undefined,
            type,
        };
        claimEditorStore?.updateProviders([...currentProviders, newProvider]);
    }, [claimEditorStore]);

    const handleDeleteProvider = useCallback((index: number) => {
        const currentProviders = claimEditorStore?.providers || [];
        const updatedProviders = currentProviders.filter((_, i) => i !== index);
        claimEditorStore?.updateProviders(updatedProviders);
    }, [claimEditorStore]);

    const handleUpdateProvider = useCallback((index: number, provider: CreateClaimProvider) => {
        const currentProviders = claimEditorStore?.providers || [];
        const updatedProviders = [...currentProviders];
        updatedProviders[index] = provider;
        claimEditorStore?.updateProviders(updatedProviders);
    }, [claimEditorStore]);

    const providers = claimEditorStore?.providers || [];
    const hasRendering = providers.some(p => p.type === CreateClaimProviderType.RENDERING);
    const hasReferring = providers.some(p => p.type === CreateClaimProviderType.REFERRING);
    const hasSupervising = providers.some(p => p.type === CreateClaimProviderType.SUPERVISING);
    const hasOrdering = providers.some(p => p.type === CreateClaimProviderType.ORDERING);

    return (
        <S.FormContainer>
            <CS.ButtonContainer>
                <BaseButton 
                    variant="primary" 
                    onClick={() => handleAddProvider(CreateClaimProviderType.RENDERING)} 
                    disabled={hasRendering}
                >
                    + Add Rendering
                </BaseButton>
                <BaseButton 
                    variant="primary" 
                    onClick={() => handleAddProvider(CreateClaimProviderType.REFERRING)} 
                    disabled={hasReferring}
                >
                    + Add Referring
                </BaseButton>
                <BaseButton 
                    variant="primary" 
                    onClick={() => handleAddProvider(CreateClaimProviderType.SUPERVISING)} 
                    disabled={hasSupervising}
                >
                    + Add Supervising
                </BaseButton>
                <BaseButton 
                    variant="primary" 
                    onClick={() => handleAddProvider(CreateClaimProviderType.ORDERING)} 
                    disabled={hasOrdering}
                >
                    + Add Ordering
                </BaseButton>
            </CS.ButtonContainer>

            {providers.map((provider, index) => (
                <ProviderSection
                    key={`${provider.type}-${index}`}
                    providerType={provider.type}
                    title={provider.type}
                    existingProvider={provider}
                    index={index}
                    onDelete={handleDeleteProvider}
                    onUpdateProvider={handleUpdateProvider}
                    claimEditorStore={claimEditorStore}
                />
            ))}
        </S.FormContainer>
    );
});

interface ProviderSectionProps {
    providerType: CreateClaimProviderType;
    title: string;
    existingProvider: CreateClaimProvider;
    index: number;
    onDelete: (index: number) => void;
    onUpdateProvider: (index: number, provider: CreateClaimProvider) => void;
    claimEditorStore: ClaimEditorStore;
}

const ProviderSection: React.FunctionComponent<ProviderSectionProps> = observer(
    ({ providerType, title, index, onDelete, onUpdateProvider, existingProvider, claimEditorStore }) => {
        const formikRef = useRef<FormikProps<ProviderFormValues> | null>(null);
        const isMountedRef = useRef(false);

        const getInitialValues = useCallback((): ProviderFormValues => {
            if (existingProvider) {
                const displayName =
                    [existingProvider.first, existingProvider.last].filter(Boolean).join(' ') ||
                    existingProvider.organization ||
                    '';
                return {
                    providerLookup: displayName,
                    organization: existingProvider.organization || '',
                    first: existingProvider.first || '',
                    last: existingProvider.last || '',
                    npi: existingProvider.npi || '',
                    phone: existingProvider.phone || '',
                    fax: existingProvider.fax || '',
                    providerId: (existingProvider.providerId as any) || undefined,
                };
            }
            return { ...initialValues, providerLookup: '' };
        }, [existingProvider]);

        const convertToProvider = (values: ProviderFormValues): CreateClaimProvider => {
            return {
                organization: values.organization || undefined,
                first: values.first || undefined,
                middle: existingProvider.middle || undefined,
                last: values.last || undefined,
                suffix: existingProvider.suffix || undefined,
                npi: values.npi || undefined,
                phone: values.phone || undefined,
                fax: values.fax || undefined,
                providerId: (values.providerId as any) || existingProvider.providerId || undefined,
                type: providerType,
            };
        };

        const debouncedSave = useCallback(
            _.debounce(() => {
                const latestValues = formikRef.current?.values;
                if (!latestValues) return;
                const providerData = convertToProvider(latestValues);
                onUpdateProvider(index, providerData);
            }, 500),
            [onUpdateProvider, index],
        );

        const handleProviderSelect = useCallback(
            (provider: SupportProvider, formik: FormikProps<ProviderFormValues>) => {
                const displayName =
                    [provider.first, provider.last].filter(Boolean).join(' ') || provider.organization || '';
                const newValues: ProviderFormValues = {
                    providerLookup: displayName,
                    organization: provider.organization || '',
                    first: provider.first || '',
                    last: provider.last || '',
                    npi: provider.npi || '',
                    phone: provider.phone || '',
                    fax: provider.fax || '',
                    providerId: (provider.id as any) || undefined,
                };

                formik.setValues(newValues);
            },
            [],
        );

        useEffect(() => {
            return () => {
                debouncedSave.cancel();
            };
        }, [debouncedSave]);

        return (
            <CS.SectionContainer>
                <CS.SectionHeader>
                    <CS.SectionTitle>{title}</CS.SectionTitle>
                    <BaseButton variant="warning" onClick={() => onDelete(index)}>✕ Delete {title} Provider</BaseButton>
                </CS.SectionHeader>

                <Formik
                    innerRef={formikRef as any}
                    initialValues={getInitialValues()}
                    validationSchema={ProviderSchema}
                    onSubmit={() => {
                        // No-op, we auto-save
                    }}
                    enableReinitialize
                    validateOnMount={false}
                    validateOnChange={true}
                    validateOnBlur={false}
                >
                    {formik => {
                        useEffect(() => {
                            if (!isMountedRef.current) {
                                isMountedRef.current = true;
                                return;
                            }
                            debouncedSave();
                        }, [formik.values, debouncedSave]);

                        useEffect(() => {
                            if (claimEditorStore.validationSignal > 0) {
                                formik.submitForm();
                            }
                        }, [claimEditorStore.validationSignal]);

                        return (
                            <>
                                <FormErrorBanner
                                    submitCount={formik.submitCount}
                                    isValid={formik.isValid}
                                    errors={formik.errors}
                                />
                                <S.FormGrid>
                                    <ProviderAutocomplete
                                        providerType={providerType}
                                        formik={formik}
                                        onSelect={(p: SupportProvider) => handleProviderSelect(p, formik)}
                                        claimEditorStore={claimEditorStore}
                                    />
                                    <S.FormField>
                                    <S.Label required={!formik.values.last.trim() || !formik.values.first.trim()}>Organization Name</S.Label>
                                        <Field
                                            name="organization"
                                            placeholder="Organization Name"
                                            component={InputField}
                                        />
                                    </S.FormField>

                                    <S.FormField>
                                        <S.Label required={!formik.values.organization.trim()}>First Name</S.Label>
                                        <Field
                                            name="first"
                                            placeholder="First Name"
                                            component={InputField}
                                        />
                                    </S.FormField>

                                    <S.FormField>
                                    <S.Label required={!formik.values.organization.trim()}>Last Name</S.Label>
                                        <Field
                                            name="last"
                                            placeholder="Last Name"
                                            component={InputField}
                                        />
                                    </S.FormField>

                                    <S.FormField>
                                        <S.Label required>NPI</S.Label>
                                        <Field
                                            name="npi"
                                            placeholder="NPI"
                                            component={InputField}
                                            maxLength={10 as any}
                                        />
                                    </S.FormField>
                                    <S.FormField>
                                        <S.Label>Phone</S.Label>
                                        <Field
                                            name="phone"
                                            placeholder="Phone"
                                            component={InputField}
                                        />
                                    </S.FormField>
                                    <S.FormField>
                                        <S.Label>Fax</S.Label>
                                        <Field
                                            name="fax"
                                            placeholder="Fax"
                                            component={InputField}
                                        />
                                    </S.FormField>
                                </S.FormGrid>
                            </>
                        );
                    }}
                </Formik>
            </CS.SectionContainer>
        );
    },
);

